class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      const html = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      // Never write undefined/null into the DOM — that is what produced a
      // blank drawer when a section failed to render.
      if (html === null) return;
      sectionElement.innerHTML = html;
    });

    const sectionHtml = parsedState.sections && parsedState.sections['cart-drawer'];
    if (sectionHtml) {
      const freshHtml = new DOMParser().parseFromString(sectionHtml, 'text/html');
      const freshCartDrawer = freshHtml.querySelector('cart-drawer');
      this.classList.toggle(
        'is-empty',
        freshCartDrawer ? freshCartDrawer.classList.contains('is-empty') : false
      );
    } else {
      this.classList.remove('is-empty');
    }

    setTimeout(() => {
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) overlay.addEventListener('click', this.close.bind(this));
      // NOTE: the success toast is no longer fired from here. renderContents()
      // runs for quantity changes and removals too, so firing from this point
      // showed "Product added to cart successfully" when an item was REMOVED.
      // It is now driven by the cart-update event instead — see the bottom of
      // this file.
    });
  }

  renderContentsAndOpen(parsedState) {
    this.renderContents(parsedState);
    setTimeout(() => this.open());
  }

  // Returns null instead of throwing when the section payload is missing or
  // the selector doesn't match. Previously this threw a TypeError on null,
  // which aborted the remaining iterations of renderContents()'s forEach and
  // left the drawer and the header count permanently out of sync until reload.
  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html) return null;
    const parsed = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return parsed ? parsed.innerHTML : null;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

// ---------------------------------------------------------------------
// Quantity button -> cart sync
// ---------------------------------------------------------------------
// IMPORTANT SCOPING RULE: this handler must only talk to the server cart
// for a card that has ALREADY been added to the cart. product-form.js adds
// the "added-item" class to a card's `.quantity-add-to-cart-btns` wrapper
// right after a successful Add to Cart submit — that class is the
// intended signal that this card's quantity stepper now represents a real
// cart line and should live-sync.
//
// The previous version of this handler ignored that signal entirely and
// fired a real /cart/add.js or /cart/change.js request on every +/- click
// ANYWHERE `.custom-quantity-input-btn-plus/-minus` appear — including
// collection-grid and search-result "quick add" cards, where a product
// should only ever be added when the person explicitly clicks "Add to
// Cart". That's what caused products to be silently added to the cart
// just from touching the quantity stepper (confirmed via network
// inspection: /cart/update.js firing before Add to Cart was ever clicked).
//
// It also had no debouncing or request-sequencing: every click re-read
// /cart.js from scratch, computed currentQty +/- 1, and fired its own
// independent fetch chain. Rapid clicks produced several overlapping
// chains reading/writing out of order, which is what caused the quantity
// visibly jumping backwards mid-click (e.g. 1 → 2 → 3 → 2 → 3 → 4).
//
// Fix: (1) only run the server-sync logic once `added-item` is present —
// before that, Dawn's built-in quantity-input component (assets/global.js)
// already handles the local +/- increment with no server involved, so
// there's nothing extra for this handler to do; (2) derive the new
// quantity from what's on screen instead of a fresh possibly-stale
// /cart.js read; (3) debounce rapid clicks and ignore any response that
// isn't for the most recent request for that variant.
const _cartSyncTimers = {};
const _cartSyncSeq = {};
const _cartSyncQty = {};       
const CART_SYNC_DEBOUNCE = 300; // matches ON_CHANGE_DEBOUNCE_TIMER used elsewhere in the theme

document.addEventListener('click', function (e) {
  const minusBtn = e.target.closest('.custom-quantity-input-btn-minus');
  const plusBtn = e.target.closest('.custom-quantity-input-btn-plus');

  if (!minusBtn && !plusBtn) return;

  // Not added to the cart yet — let Dawn's native quantity-input component
  // handle the local increment/decrement. Nothing to sync to the server.
  const addToCartContainer = e.target.closest('.quantity-add-to-cart-btns');
  if (!addToCartContainer || !addToCartContainer.classList.contains('added-item')) {
    return;
  }

  const form = e.target.closest('form');
  const variantId = parseInt(form?.querySelector('input[name="id"]')?.value, 10);
  const quantityInput = form?.querySelector('.custom-quantity-input');

  if (!variantId || !quantityInput) return;

  const min = parseInt(quantityInput.dataset.min || 1, 10);
  const max = parseInt(quantityInput.dataset.max || 99, 10);

  // Dawn's native quantity-input component (assets/global.js) is bound
  // directly to this same button. Because click events fire on their
  // target BEFORE bubbling up to ancestors, Dawn's own handler always runs
  // first and has ALREADY stepped quantityInput.value up/down by 1 (and
  // clamped it to min/max) by the time this document-level listener sees
  // the click. Adding/subtracting again here double-counts every click —
  // that's what caused quantities to jump by 2 each time (1 → 3 → 5 → 7).
  // Just read the value Dawn already set; don't step it a second time.
  let newQty = parseInt(quantityInput.value, 10);
  if (isNaN(newQty)) newQty = min;
  newQty = Math.min(Math.max(newQty, min), max);
  quantityInput.value = newQty;

  if (newQty === 0) {
    addToCartContainer.classList.remove('added-item');
    quantityInput.value = 1;
  }

  scheduleCartSync(variantId, newQty, quantityInput, addToCartContainer);
});

function scheduleCartSync(variantId, quantity, quantityInput, addToCartContainer) {
  clearTimeout(_cartSyncTimers[variantId]);
  _cartSyncQty[variantId] = quantity;   
  _cartSyncTimers[variantId] = setTimeout(() => {
    runCartSync(variantId, quantity, quantityInput, addToCartContainer);
  }, CART_SYNC_DEBOUNCE);
}
// New Change for ATC Quantity
document.addEventListener(
  'submit',
  function (e) {
    const form = e.target;
    if (!form || !form.matches || !form.matches('form[action*="/cart/add"]')) return;

    const variantId = parseInt(form.querySelector('input[name="id"]')?.value, 10);
    if (!variantId) return;
    
    if (_cartSyncQty[variantId] === 0) return;

    // Cancel the queued sync.
    clearTimeout(_cartSyncTimers[variantId]);

    // Invalidate any sync already in flight: bumping the sequence makes its
    // isStale() check true, so it discards its result instead of rendering it.
    _cartSyncSeq[variantId] = (_cartSyncSeq[variantId] || 0) + 1;
  },
  true
);


async function runCartSync(variantId, quantity, quantityInput, addToCartContainer) {
  // Tag this request with a sequence number per variant. If another click
  // schedules a newer request before this one finishes, this one's result
  // is discarded instead of being allowed to overwrite the newer state.
  const seq = (_cartSyncSeq[variantId] = (_cartSyncSeq[variantId] || 0) + 1);
  const isStale = () => _cartSyncSeq[variantId] !== seq;

  const checkoutButtons = document.querySelectorAll('button[name="checkout"]');
  checkoutButtons.forEach((btn) => {
    btn.disabled = true;
  });

  try {
    const cart = await fetch('/cart.js').then((res) => res.json());
    if (isStale()) return;

    const cartItem = cart.items.find((item) => item.id === variantId);
    const line = cartItem ? cart.items.indexOf(cartItem) + 1 : null;

    if (cartItem && line) {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({ line, quantity }),
      });
    } else {
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({ id: variantId, quantity }),
      });
    }
    if (isStale()) return;

    const sectionRes = await fetch('/?sections=cart-drawer,cart-icon-bubble');
    const sections = await sectionRes.json();
    if (isStale()) return;

    const cartDrawer = document.querySelector('cart-drawer');

    if (cartDrawer?.renderContents) {
      cartDrawer.renderContents({
        sections: {
          'cart-drawer': sections['cart-drawer'],
          'cart-icon-bubble': sections['cart-icon-bubble'],
        },
      });
    }

    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items' });
    }
  } catch (err) {
    console.error('Cart update failed:', err);
    checkoutButtons.forEach((btn) => {
      btn.disabled = false;
    });
  } finally {
    if (isStale()) return;
    if (!window._gwpRunning) {
      checkoutButtons.forEach((btn) => {
        btn.disabled = false;
      });
    }
  }
}

// ---------------------------------------------------------------------
// Success toast (CRO-003)
// ---------------------------------------------------------------------
// This was fully written but left entirely commented out, so the two live
// call sites (this file and recently-viewed-product.js) were silently
// no-oping and customers got no confirmation on Add to Cart.

// window.showCartToast = function (message) {
//   message = message || 'Product added to cart successfully';

//   if (!document.getElementById('cart-toast-styles')) {
//     const style = document.createElement('style');
//     style.id = 'cart-toast-styles';
//     style.textContent =
//       '.cart-toast{position:fixed;bottom:25px;left:50%;' +
//       'transform:translateX(-50%) translateY(-24px);background:#1d1d1d;color:#fff;' +
//       'padding:12px 18px;border-radius:6px;font-size:14px;line-height:1.2;' +
//       "font-family:'Work Sans',Helvetica,Arial,sans-serif;z-index:100001;" +
//       'box-shadow:0 4px 16px rgba(0,0,0,.22);display:flex;align-items:center;gap:9px;' +
//       'opacity:0;transition:opacity .3s ease,transform .3s ease;pointer-events:none;max-width:92vw;}' +
//       '.cart-toast.is-visible{opacity:1;transform:translateX(-50%) translateY(0);}' +
//       '.cart-toast__check{display:inline-flex;width:20px;height:20px;border-radius:50%;' +
//       'background:#2ecc71;color:#fff;align-items:center;justify-content:center;' +
//       'font-size:12px;font-weight:700;flex-shrink:0;}' +
//       '@media (prefers-reduced-motion: reduce){.cart-toast{transition:none;}}';
//     document.head.appendChild(style);
//   }

//   let toast = document.getElementById('cart-toast');
//   if (!toast) {
//     toast = document.createElement('div');
//     toast.id = 'cart-toast';
//     toast.className = 'cart-toast';
//     toast.setAttribute('role', 'status');
//     toast.setAttribute('aria-live', 'polite');
//     document.body.appendChild(toast);
//   }

//   toast.innerHTML = '<span class="cart-toast__check">\u2713</span><span class="cart-toast__text"></span>';
//   toast.querySelector('.cart-toast__text').textContent = message;

//   requestAnimationFrame(() => toast.classList.add('is-visible'));

//   clearTimeout(window.__cartToastTimer);
//   window.__cartToastTimer = setTimeout(() => {
//     toast.classList.remove('is-visible');
//   }, 2500);
// };

// Fire the toast ONLY on a genuine add, never on quantity changes or removals.
// pubsub.js and constants.js are both deferred and load before this file, so
// subscribe/PUB_SUB_EVENTS are already defined here.
if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
  subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
    if (!event || event.source !== 'product-form') return;
    // The sticky ATC sets this flag because it is about to open the drawer —
    // showing a toast on top of an opening drawer is redundant noise.
    if (window.__crowaiOpeningDrawer) return;
    window.showCartToast();
  });
}